import { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import "./App.css";
import PrivateRoute from "./auth/PrivateRoute";
import RedirectToHomeIfLoggedIn from "./auth/RedirectToHomeIfLoggedIn";
import Account from "./pages/account/Account";
import Geographic from "./pages/account/components/Geographic";
import Refer from "./pages/account/components/Refer";
import YourProfile from "./pages/account/components/YourProfile";
import Home from "./pages/home/Home";
import HomePs from "./pages/home/HomePs";
import KnowMore from "./pages/knowMore/KnowMore";
import OnBoarding from "./pages/onBoarding/OnBoarding";
import PersonalDetails from "./pages/onBoarding/PersonalDetails";
import Speciality from "./pages/onBoarding/Speciality";
import PatientDetails from "./pages/patientDetails/PatientDetails";
import Patients from "./pages/patients/Patients";
import PatientsLogs from "./pages/patientsLogs/PatientsLogs";
import AlreadyReferred from "./pages/referCarePlan/AlreadyReferred";
import Congratulations from "./pages/referCarePlan/Congratulations";
import PatientCondition from "./pages/referCarePlan/PatientCondition";
import PatientName from "./pages/referCarePlan/PatientName";
import PatientPhoneNo from "./pages/referCarePlan/PatientPhoneNo";
import PatientSummary from "./pages/referCarePlan/PatientSummary";
import SelectCareProgram from "./pages/referCarePlan/SelectCareProgram";
import Rewards from "./pages/rewards/Rewards";
import TatvaAi from "./pages/tatvaAi/TatvaAi";
import HomePage from "./pages/tatvaShots/components/HomePage";
import MyMainComponent from "./pages/tatvaShots/components/MainComp";
import PreferenceSettings from "./pages/tatvaShots/components/PreferenceSettings";
import TatvaShots from "./pages/tatvaShots/TatvaShots";
import useStore from "./store/useStore";
import { AUTH_TOKEN, FROM_TATAV_PRACTICE, PRACTICE_TOKEN } from "./utils/constants";
import { getDecodedToken, getLocalStorage, setLocalStorage } from "./utils/localStorage";
import { initAppConfig, getAppName } from "./utils/appConfig";
import Download from "./pages/download/Download";
import Goodflip from "./pages/goodflip/Goodflip";
import DDxWelcomePage from "./pages/ddx-welcome/DDxWelcomePage";
import GenerateDDxPage from "./pages/generateDDx/GenerateDDxPage";
import DDxAboutPage from "./pages/about-ddx/DDxAboutPage";
import ContentScreen from "./pages/tatvaPedia/ContentScreen";
import CommentThread from "./pages/tatvaPedia/CommentThread";
import TatvaPediaDocument from "./pages/tatvaPedia/TatvaPediaPDF";
import HomeScreen from "./pages/tatvaPedia/TatvaPediaHomeScreen";
// import Filter from "./pages/tatvaPedia/Filter";
import PersonalizeFeed from "./pages/tatvaPedia/PersonalizeFeed";
import Tatvapedia from "./pages/tatvaPedia/TatvaPedia";
import PediaSearch from "./pages/tatvaPedia/PediaSearch";
import PediaLibrary from "./pages/tatvaPedia/PediaLibrary";
import ContentListSection from "./pages/tatvaPedia/components/ContentListSection";
import FormatContentList from "./pages/tatvaPedia/FormatContentList";
import TatvaPediaRouteGuard from "./pages/tatvaPedia/TatvaPediaRouteGuard";
import SymptomCollector from "./pages/symptomCollector/SymptomCollector";
import Summary from "./pages/symptomCollector/components/summary/Summary";
import Conversation from "./pages/symptomCollector/components/conversation/Conversation";
import TakeQuiz from "./pages/quiz/TakeQuiz";
import QuizResult from "./pages/quiz/QuizResult";
import PediaQuiz from "./pages/tatvaPedia/PediaQuiz";
import SessionExceed from "./pages/session-exceed/SessionExceed";
import SelectLanguage from "./pages/appointmentAgent/components/selectLanguage/SelectLanguage";
import BookAppointment from "./pages/appointmentAgent/components/bookAppointment/BookAppointment";
import SuccessAppointment from "./pages/appointmentAgent/components/successAppointment/SuccessAppointment";
import HomeAppointment from "./pages/appointmentAgent/components/homeAppointment/HomeAppointment";
import { useToast } from "./components/Toast";
import NativeUpdateBanner from "./components/NativeUpdateBanner";
import PublicContentPreview from "./pages/tatvaPedia/PublicContentPreview";
import PrescriptionDetails from "./pages/appointmentAgent/components/prescriptionDetails/PrescriptionDetails";
import useSwipeNavigation from "./hooks/useSwipeNavigation";
import { withSentryReactRouterV6Routing } from "@sentry/react";

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

function App() {
  const location = useLocation();
  const urlSearchString = window.location.search;
  const params = new URLSearchParams(urlSearchString);
  const authToken: string | null = params.get("authToken");
  const tatvaPracticeToken: string | null = params.get("practiceToken");
  const fromTatvaPractice: string | null = params.get("fromPractice");
  const { showToast, ToastComponent } = useToast();

  // Handle swipe left navigation and browser back button
  useSwipeNavigation();

  const { doctorDetails, setDoctorDetails } = useStore((state) => ({
    doctorDetails: state.doctorDetails,
    setDoctorDetails: state.setDoctorDetails,
  }));
  const scrollToTop = () => {
    const element = document.getElementById("app-scroll");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };
  useEffect(() => {
    if (authToken) {
      setLocalStorage(AUTH_TOKEN, authToken);
    }
    if (tatvaPracticeToken) {
      setLocalStorage(PRACTICE_TOKEN, tatvaPracticeToken);
    }
    if (fromTatvaPractice) {
      setLocalStorage(FROM_TATAV_PRACTICE, fromTatvaPractice);
    }
  }, [authToken]);

  // Initialize app configuration on component mount
  useEffect(() => {
    // Detect and persist the app type when the application loads
    initAppConfig();

    // Set document title and meta description based on app configuration
    const updateDocumentMeta = () => {
      const appName = getAppName();
      document.title = appName;
    };

    // Update meta immediately and also after a short delay to ensure app config is loaded
    updateDocumentMeta();
    setTimeout(updateDocumentMeta, 100);
  }, []);

  useEffect(() => {
    scrollToTop();
  }, [location]);

  useEffect(() => {
    if (getLocalStorage(AUTH_TOKEN)) {
      const decodedToken = getDecodedToken();
      setDoctorDetails({ ...doctorDetails, ...decodedToken });
    }
  }, []);

  useEffect(() => {
    /*
     * Publishes 1% of the *visible* viewport height as --vh, which the TatvaShots feed
     * sizes its full-screen cards against.
     *
     * This previously called setProperty("100dvh", ...). A name that is neither a custom
     * property nor a real CSS property is silently ignored, so --vh was never actually
     * set and every `calc(var(--vh, 1vh) * 100)` fell back to plain `100vh`. On iOS
     * `100vh` is the *large* viewport — it excludes the dynamic toolbars — so the feed
     * rendered taller than the .App scroll container (100dvh) and .App became
     * scrollable. That stray scroll competed with the swipe gesture, which is why the
     * feed scrolled and snapped unreliably on some iPads and not others.
     *
     * It also has to match window.innerHeight, because SwipableShorts animates by
     * exactly that many pixels; when the card height and the slide distance disagree the
     * next card cannot land flush.
     */
    const handleResize = () => {
      const height = window.innerHeight;
      document.documentElement.style.setProperty("--vh", `${height * 0.01}px`);
    };

    // Set initial height
    handleResize();
    window.addEventListener("resize", handleResize);
    // iOS collapses/expands its toolbars without always firing `resize`; visualViewport
    // is what actually reports that, and it is the case where the two heights diverge.
    window.visualViewport?.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("resize", handleResize);
    };
  }, []);

  // Make toast available globally
  useEffect(() => {
    (window as any).showAppToast = showToast;
  }, [showToast]);

  return (
    <div
      id="app-scroll"
      className="App"
      style={{ height: `calc(var(100dvh, 1vh) * 100)` }}
    >
      <NativeUpdateBanner />
      <div className="bg-white">
        <SentryRoutes>
          <Route
            path="on-boarding"
            element={
              <RedirectToHomeIfLoggedIn>
                {(window as any)?.ReactNativeWebView ? <></> : <OnBoarding />}
              </RedirectToHomeIfLoggedIn>
            }
          />
          <Route path="personal-details" element={<PersonalDetails />} />
          <Route path="speciality" element={<Speciality />} />
          <Route path="download" element={<Download />} />
          <Route path="geo" element={<Geographic />} />
          <Route path="refer" element={<Refer />} />
          <Route path="symptoms-collector" element={<SymptomCollector />} />
          <Route path="conversation" element={<Conversation />} />
          <Route path="symptoms-collector-summary" element={<Summary />} />
          <Route path="home-appointment" element={<SelectLanguage />} />
          <Route path="book-appointment" element={<BookAppointment />} />
          <Route path="success-appointment" element={<SuccessAppointment />} />
          <Route path="appointment-booking" element={<HomeAppointment />} />
          <Route
            path="prescription-details"
            element={<PrescriptionDetails />}
          />
          <Route
            path="/preview/:appName/:contentType/:contentId"
            element={<PublicContentPreview />}
          />
          <Route
            path="/*"
            element={
              <PrivateRoute>
                <Routes>
                  <Route path="" element={<HomePs />} />
                  <Route path="tatva-ai" element={<TatvaAi />} />
                  <Route path="short" element={<MyMainComponent />} />
                  <Route path="remote-care" element={<Home />} />
                  <Route path="tatva-shots" element={<TatvaShots />} />
                  <Route path="tatva-shots-home" element={<HomePage />} />
                  <Route path="preferences" element={<PreferenceSettings />} />
                  <Route path="congratulations" element={<Congratulations />} />
                  <Route
                    path="patient-already-referred"
                    element={<AlreadyReferred />}
                  />
                  <Route path="patient-details" element={<PatientDetails />} />
                  <Route path="rewards" element={<Rewards />} />
                  <Route path="patients" element={<Patients />} />
                  <Route path="patient-logs" element={<PatientsLogs />} />
                  <Route path="account" element={<Account />} />
                  <Route path="profile" element={<YourProfile />} />

                  <Route
                    path="patient-condition"
                    element={<PatientCondition />}
                  />
                  <Route
                    path="patient-care-program"
                    element={<SelectCareProgram />}
                  />
                  <Route
                    path="patient-phone-number"
                    element={<PatientPhoneNo />}
                  />
                  <Route path="patient-name" element={<PatientName />} />
                  <Route path="patient-summary" element={<PatientSummary />} />
                  <Route path="know-more" element={<KnowMore />} />
                  <Route path="patient-progress" element={<Goodflip />} />
                  <Route path="ddx-welcome" element={<DDxWelcomePage />} />
                  <Route path="generate-ddx" element={<GenerateDDxPage />} />
                  <Route path="about-ddx" element={<DDxAboutPage />} />
                  <Route path="content" element={<ContentScreen />} />
                  <Route path="comment-thread" element={<CommentThread />} />
                  <Route
                    path="tatvaPedia-document"
                    element={<TatvaPediaDocument />}
                  />
                  <Route path="tatvapedia" element={<Tatvapedia />}>
                    <Route
                      index
                      element={
                        <TatvaPediaRouteGuard>
                          <HomeScreen />
                        </TatvaPediaRouteGuard>
                      }
                    />
                    <Route
                      path="personalize-feed"
                      element={<PersonalizeFeed />}
                    />
                    <Route path="search" element={<PediaSearch />} />
                    <Route path="library" element={<PediaLibrary />} />
                    {/* <Route path="filters" element={<Filter />} /> */}
                    <Route
                      path="content-list"
                      element={<ContentListSection />}
                    />
                    <Route path="quiz" element={<TakeQuiz />} />
                    <Route path="quiz-result" element={<QuizResult />} />
                    <Route path="quiz-list" element={<PediaQuiz />} />
                    <Route
                      path="format-content-list"
                      element={<FormatContentList />}
                    />
                  </Route>
                  <Route path="session-exceed" element={<SessionExceed />} />
                </Routes>
              </PrivateRoute>
            }
          />
        </SentryRoutes>
      </div>

      {/* Toast Component */}
      {ToastComponent}
    </div>
  );
}

export default App;
